<!-- eslint-disable vue/valid-v-on -->
<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <!-- =======================
         NAVBAR
    ======================== -->
    <nav class="navbar" ref="navbar">
      <div class="logo-container">
        <div class="logo">
          <router-link to="/" class="logo-link" aria-label="Accueil SpeDev">
            <img src="@/assets/logo_spedev_67cada.png" alt="SpeDev" class="logo-img" />
          </router-link>
        </div>
      </div>

      <!-- Burger -->
      <div class="hamburger" ref="hamburgerBtn" @click="toggleMenu" aria-label="Ouvrir le menu"
        :aria-expanded="menuOpen">
        <span :class="{ 'rotate1': menuOpen }"></span>
        <span :class="{ 'fade': menuOpen }"></span>
        <span :class="{ 'rotate2': menuOpen }"></span>
      </div>

      <!-- Liens -->
      <ul class="nav-links" ref="navLinks" :class="{ 'nav-active': menuOpen }" @click.self="closeMenu">
        <li>
          <a href="#home" @click.prevent="goToAnchor('#home')" :class="{ active: activeLink === '#home' }">Accueil</a>
        </li>
        <li>
          <a href="#projects" @click.prevent="goToAnchor('#projects')"
            :class="{ active: activeLink === '#projects' }">Projets</a>
        </li>
        <li>
          <a href="#about" @click.prevent="goToAnchor('#about')" :class="{ active: activeLink === '#about' }">À
            propos</a>
        </li>
        <li>
          <a href="#contact1" @click.prevent="goToAnchor('#contact1')"
            :class="{ active: activeLink === '#contact1' }">Contact</a>
        </li>

        <li>
          <a @click.prevent="openServices" :class="{ active: isServicesActive }">Services</a>
        </li>

        <!-- Bouton thème -->
        <li class="li-darkmode">
          <button class="btn-darkmode" @click.stop="toggleDarkMode"
            :aria-label="darkMode ? 'Passer en mode clair' : 'Passer en mode sombre'" title="Changer de thème">
            <span v-if="darkMode" class="icon-sun" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            </span>
            <span v-else class="icon-moon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            </span>
          </button>
          <span class="darkmode-label">Thème</span>
        </li>

        <li>
          <button class="btn-projects" @click="openProjects">Voir nos projets</button>
        </li>
      </ul>

      <!-- Barre de progression collée sous la navbar -->
      <div class="nav-progress" aria-hidden="true">
        <span class="nav-progress__bar" :style="progressStyle"></span>
      </div>
    </nav>

    <!-- =======================
         CONTENU PRINCIPAL
    ======================== -->
    <main v-if="$route.path === '/'">
      <!-- =======================
           HERO
      ======================== -->
      <section id="home">
        <section class="hero hero--spe">
          <!-- ====== SLIDER HERO ====== -->
          <div class="hero-slider" data-anim="fade" style="--d:0ms" @mouseenter="pauseAuto" @mouseleave="resumeAuto"
            @keydown.left.prevent="prevSlide" @keydown.right.prevent="nextSlide" tabindex="0" ref="heroSlider"
            aria-live="polite">
            <!-- Piste empilée -->
            <div class="hero-slider__track" @pointerdown="onPointerDown" @pointermove="onPointerMove"
              @pointerup="onPointerUp" @pointercancel="onPointerUp" @touchstart.passive @touchmove.prevent
              @touchend.passive>
              <div v-for="(img, i) in heroSlides" :key="img.src" class="hero-slider__slide"
                :class="{ 'is-active': i === currentSlide, 'is-prev': i === prevIndex, 'is-next': i === nextIndex }"
                role="group" :aria-roledescription="'slide'" :aria-label="`${i + 1} / ${heroSlides.length}`"
                :id="`slide-${i}`">
                <img class="hero__banner" :src="img.src" :alt="img.alt" loading="eager" decoding="async" />
              </div>
            </div>

            <!-- Flèches -->
            <button class="hero-slider__arrow hero-slider__arrow--prev" @click="prevSlide"
              aria-label="Slide précédent">‹</button>
            <button class="hero-slider__arrow hero-slider__arrow--next" @click="nextSlide"
              aria-label="Slide suivant">›</button>

            <!-- Puces -->
            <div class="hero-slider__dots" role="tablist" aria-label="Choisir une diapositive">
              <button v-for="(img, i) in heroSlides" :key="`dot-${i}`" class="hero-slider__dot"
                :class="{ 'is-active': i === currentSlide }" role="tab" :aria-selected="i === currentSlide"
                :aria-controls="`slide-${i}`" @click="goTo(i)">
                <span class="sr-only">Aller au slide {{ i + 1 }}</span>
              </button>
            </div>
          </div>

          <div class="hero__chip" data-anim="zoom-in" style="--d:120ms">
            Votre partenaire digital
          </div>

          <h1 class="hero__title" data-anim="fade-up" style="--d:200ms">
            <span class="hero__title-strong">SpeDev</span>
            <span class="hero__title-accent"> Transformation digitale</span>
          </h1>

          <p class="hero__subtitle" data-anim="fade-up" style="--d:300ms">
            Développement web & mobile, logiciels métier, UI/UX. <br />
            Nous transformons vos idées en produits fiables et performants.
          </p>

          <!-- ====== EXPERTISES ====== -->
          <h2 class="hero__block-title" data-anim="fade-up" style="--d:360ms">Nos expertises</h2>

          <div class="hero__benefits" role="list">
            <article v-for="(s, i) in sectors" :key="s.id" :id="`sector-${s.id}`" class="benefit" role="button"
              tabindex="0" :aria-expanded="openSectorId === s.id" @mouseenter="onHoverIn(s.id)"
              @mouseleave="onHoverOut(s.id)" @focus="onHoverIn(s.id)" @blur="onHoverOut(s.id)"
              @click="onToggleMobile(s.id)" @keydown.enter.prevent="onToggleMobile(s.id)"
              @keydown.space.prevent="onToggleMobile(s.id)" :data-anim="'fade-up'" :style="{ '--d': (i * 80) + 'ms' }">
              <div class="benefit__head">
                <span class="benefit__title">{{ s.title }}</span>
              </div>

              <!-- Popover / Accordéon -->
              <div class="benefit__popover" v-show="openSectorId === s.id"
                :class="{ 'benefit__popover--visible': openSectorId === s.id }" @click.stop>
                <ul class="benefit__list">
                  <li v-for="(b, k) in s.points" :key="k">{{ b }}</li>
                </ul>
                <button class="benefit__close" @click="closePopover" aria-label="Fermer le détail">OK</button>
              </div>
            </article>
          </div>

          <a href="#projects" class="hero__cta" @click.prevent="goToAnchor('#projects')" data-anim="fade-up"
            style="--d:580ms">
            Découvrir nos projets
          </a>
        </section>
      </section>

      <!-- ======================= ABOUT ======================== -->
      <section id="about">
        <section class="spedev-abt-section">
          <div class="spedev-abt-wrap">
            <div class="spedev-abt-badge" data-anim="zoom-in" style="--d:0ms">À propos de nous</div>
            <h2 class="spedev-abt-title" data-anim="fade-up" style="--d:120ms">
              Nous <span class="spedev-abt-title-accent">transformons vos idées</span> en réalités numériques durables
            </h2>
            <p class="spedev-abt-subtitle" data-anim="fade-up" style="--d:220ms">
              Chez <strong>SpeDev</strong>, nous ne faisons pas que coder : nous bâtissons des solutions qui portent vos
              ambitions,
              traduisent vos rêves en code et transforment vos projets en succès tangibles.
            </p>
            <div class="spedev-abt-grid">
              <article class="spedev-abt-card spedev-abt-card--mint" data-anim="fade-up" style="--d:0ms">
                <div class="spedev-abt-illus" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="72" height="72">
                    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.12" />
                    <path d="M7 13l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
                <h3 class="spedev-abt-card-title">Notre conviction</h3>
                <p class="spedev-abt-card-text">Vision claire, partenaire fiable et équipe engagée, pour faire briller
                  votre projet.</p>
              </article>
              <article class="spedev-abt-card spedev-abt-card--lilac" data-anim="fade-up" style="--d:80ms">
                <div class="spedev-abt-illus" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="72" height="72">
                    <rect x="3" y="4" width="18" height="14" rx="2" ry="2" fill="currentColor" opacity="0.12" />
                    <path d="M7 8h10M7 12h6M7 16h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </div>
                <h3 class="spedev-abt-card-title">Expertise & portée</h3>
                <p class="spedev-abt-card-text"><strong>Couverture internationale</strong> avec sens du contexte local.
                </p>
              </article>
              <article class="spedev-abt-card spedev-abt-card--blue" data-anim="fade-up" style="--d:160ms">
                <div class="spedev-abt-illus" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="72" height="72">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.9L18.18 22 12 18.77 5.82 22 7 14.17l-5-4.9 6.91-1.01L12 2z"
                      fill="currentColor" opacity="0.12" />
                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
                <h3 class="spedev-abt-card-title">Engagement total</h3>
                <p class="spedev-abt-card-text"><strong>Qualité</strong>, <strong>innovation</strong> &
                  <strong>fiabilité</strong>.
                </p>
              </article>
              <article class="spedev-abt-card spedev-abt-card--rose" data-anim="fade-up" style="--d:240ms">
                <div class="spedev-abt-illus" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="72" height="72">
                    <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.12" />
                    <path d="M8 12h8M12 8v8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </div>
                <h3 class="spedev-abt-card-title">Sur-mesure</h3>
                <p class="spedev-abt-card-text">De l’idéation au scale, une trajectoire adaptée à vos objectifs.</p>
              </article>
              <article class="spedev-abt-card spedev-abt-card--sage" data-anim="fade-up" style="--d:320ms">
                <div class="spedev-abt-illus" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="72" height="72">
                    <path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    <rect x="3" y="4" width="18" height="16" rx="3" ry="3" fill="currentColor" opacity="0.08" />
                  </svg>
                </div>
                <h3 class="spedev-abt-card-title">Ce que nous bâtissons</h3>
                <p class="spedev-abt-card-text">Sites, apps & APIs robustes, performantes et sécurisées.</p>
              </article>
              <article class="spedev-abt-card spedev-abt-card--sky" data-anim="fade-up" style="--d:400ms">
                <div class="spedev-abt-illus" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="72" height="72">
                    <path d="M12 20c4.418 0 8-3.582 8-8" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" />
                    <path d="M12 4C7.582 4 4 7.582 4 12" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" />
                    <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.18" />
                  </svg>
                </div>
                <h3 class="spedev-abt-card-title">Pourquoi SpeDev</h3>
                <p class="spedev-abt-card-text">Des <strong>bâtisseurs</strong> pour des produits qui durent.</p>
              </article>
            </div>
            <div class="spedev-abt-cta" data-anim="fade-up" style="--d:520ms">
              <p>Nous ne faisons pas que développer. <strong>Nous façonnons l’avenir avec vous.</strong></p>
            </div>
          </div>
        </section>
      </section>

      <!-- =======================
           PROJECTS
      ======================== -->
      <section id="projects" class="projects">
        <header class="projects__head" data-anim="fade-up" style="--d:0ms">
          <div class="project-chip">Nos projets phares</div>
          <h1 class="projects__title">Parcourez nos <span class="projects__title-accent">projets en action</span></h1>
          <p class="projects__subtitle">Des solutions réelles qui allient performance, design et fiabilité.</p>
        </header>

        <div class="projects__grid">
          <article v-for="(project, i) in displayedProjects" :key="project.id" class="project" data-anim="fade-up"
            :style="{ '--d': (i * 80) + 'ms' }">
            <div class="project__thumb">
              <img :src="project.images?.[0]" :alt="project.title" loading="lazy" />
            </div>
            <div class="project__body">
              <h3 class="project__title">{{ project.title }}</h3>
              <p class="project__desc" v-html="linkify(project.description)"></p>
              <router-link class="cta" :to="`/projects/${project.slug}`">
                <span class="project__cta">En savoir +</span>
              </router-link>
            </div>
          </article>
        </div>

        <div class="projects__more" v-if="projects && projects.length >= 4" data-anim="fade-up" style="--d:480ms">
          <router-link to="/projects" class="btn-more">Voir plus >></router-link>
        </div>
      </section>

      <!-- =======================
           CONTACT
      ======================== -->
      <section class="contact1" id="contact1">
        <section class="contact-hero" id="contact-top" data-anim="fade-up" style="--d:0ms">
          <p class="contact-chip">Contactez-nous</p>
          <h1 class="contact-title">Parlons de votre <span class="contact-grad">projet</span></h1>
          <p class="contact-subtitle">Notre équipe est prête à répondre à vos questions et à discuter de vos besoins.
          </p>
        </section>

        <section id="contact" class="contact">
          <div class="contact__grid">
            <!-- Coordonnées -->
            <aside class="contact__info" data-anim="slide-right" style="--d:100ms">
              <div class="coor">
                <div class="bullet bullet--pin">
                  <FontAwesomeIcon :icon="['fas', 'map-pin']" />
                </div>
                <h3 class="contact__heading">Nos coordonnées</h3>
              </div>

              <div class="info-block">
                <div class="info-block__icon">
                  <div class="bullet1">
                    <FontAwesomeIcon :icon="['fas', 'map-pin']" />
                  </div>
                </div>
                <div>
                  <div class="info-block__label">Bureaux</div>
                  <ul class="info-block__list">
                    <li>Yaoundé, Cameroun</li>
                  </ul>
                </div>
              </div>

              <div class="info-block">
                <div class="info-block__icon">
                  <div class="bullet1">
                    <FontAwesomeIcon :icon="['fas', 'envelope']" />
                  </div>
                </div>
                <div>
                  <div class="info-block__label">Email</div>
                  <a class="link" href="https://mail.google.com/mail/?view=cm&fs=1&to=infos@spe-store-sarl.com"
                    target="_blank" rel="noopener">infos@spe-store-sarl.com</a>
                </div>
              </div>

              <!-- WhatsApp -->
              <div class="info-block">
                <div class="info-block__icon">
                  <div class="bullet1">
                    <FontAwesomeIcon :icon="['fab', 'whatsapp']" class="wa-icon" />
                  </div>
                </div>
                <div>
                  <div class="info-block__label">WhatsApp</div>
                  <a class="link" href="https://wa.me/237621077614" target="_blank" rel="noopener">621077614</a>
                </div>
              </div>

              <hr class="sep" />

              <div class="info-block1">
                <div class="info-block__label">Heures d'ouverture</div>
                <ul class="info-block__list compact">
                  <li>Lundi - Dimanche: 8h - 20h</li>
                </ul>
              </div>
            </aside>

            <!-- Formulaire -->
            <form class="contact__form" @submit.prevent="sendMessage" data-anim="slide-left" style="--d:180ms"
              novalidate>
              <h3 class="form__title">Envoyez-nous un message</h3>

              <div class="grid-2">
                <div class="field">
                  <label>Votre nom <span class="req">*</span></label>
                  <input v-model.trim="form.name" required type="text" name="name" placeholder="Nom complet" />
                </div>
                <div class="field">
                  <label>Votre email <span class="req">*</span></label>
                  <input v-model.trim="form.email" required type="email" name="email" placeholder="vous@exemple.com" />
                </div>
              </div>

              <div class="grid-2">
                <!-- Téléphone -->
                <div class="field">
                  <label>Votre numéro</label>
                  <VueTelInput ref="telInput" v-model="form.phoneFull" v-bind="telCfg"
                    @update:modelValue="onPhoneChange" @validate="onPhoneValidate" />

                  <!-- (optionnel mais utile) un input caché pour “assurer le coup” -->
                  <input type="hidden" name="phone" :value="form.phoneE164 || form.phoneFull" />
                </div>

                <div class="field">
                  <label>Sujet</label>
                  <input v-model.trim="form.subject" type="text" name="subject" placeholder="Sujet du message" />
                </div>
              </div>

              <div class="field">
                <label>Votre message <span class="req">*</span></label>
                <textarea v-model.trim="form.message" name="message" required rows="8"
                  placeholder="Décrivez votre besoin…"></textarea>
              </div>

              <!-- 🔽🔽🔽 AJOUTER ICI LE CAPTCHA 🔽🔽🔽 -->
              <div class="field">
                <label>Vérification anti-robot</label>
                <div class="g-recaptcha" data-sitekey="6LeSwBYsAAAAAMg9pKup1b9zYFI9FVwYfYUF4JLS" data-callback="onSpeDevRecaptcha"
                  data-expired-callback="onSpeDevRecaptchaExpired">
                </div>
              </div>
              <!-- 🔼🔼🔼 FIN AJOUT 🔼🔼🔼 -->

              <!-- Anti-spam invisible (honeypot) -->
              <input type="text" name="_gotcha" v-model="form._gotcha" style="display:none" tabindex="-1"
                autocomplete="off" />
              <!-- Métadonnées utiles -->
              <input type="hidden" name="_subject" :value="`[SpeDev] ${form.subject || 'Nouveau message'}`" />
              <input type="hidden" name="_origin" value="site-spedev" />

              <button class="btn btn-grad" type="submit" :disabled="sending">
                {{ sending ? 'Envoi…' : 'Envoyer le message' }}
              </button>

              <p v-if="statusMsg" :class="['status', statusOk ? 'status--ok' : 'status--err']">
                {{ statusMsg }}
              </p>
            </form>
          </div>
        </section>
      </section>
    </main>

    <!-- Route dynamique -->
    <router-view />

    <!-- =======================
         FOOTER
    ======================== -->
    <footer class="footer" ref="footer" data-anim="fade-up" style="--d:0ms">
      <div class="footer__grid">
        <div>
          <h4 class="footer__title">Navigation</h4>
          <ul class="footer__links">
            <li><a href="#about" @click.prevent="goToAnchor('#about')">À propos</a></li>
            <li><a href="#projects" @click.prevent="goToAnchor('#projects')">Nos projets</a></li>
            <li><a href="#contact1" @click.prevent="goToAnchor('#contact1')">Contact</a></li>
          </ul>
        </div>

        <div>
          <h4 class="footer__title">Contact</h4>
          <ul class="footer__contact">
            <li class="footer-line"><span>Yaoundé, Cameroun</span></li>
            <li class="icon-line">
              <IconMail /><a href="https://mail.google.com/mail/?view=cm&fs=1&to=infos@spe-store-sarl.com"
                target="_blank" rel="noopener">infos@spe-store-sarl.com</a>
            </li>
            <li class="footer-line footer-line--wa">
              <span class="footer-line__icon" aria-hidden="true">
                <FontAwesomeIcon :icon="['fab', 'whatsapp']" class="wa-icon" />
              </span>
              <a href="https://wa.me/237621077614" target="_blank" rel="noopener">621077614</a>
            </li>
          </ul>
        </div>
      </div>

      <div class="footer__bottom">
        <p>© {{ new Date().getFullYear() }} SpeDev filiale de Spe.Store. Tous droits réservés.</p>
      </div>
    </footer>
  </div>
</template>

<script>
/* ===========================================================
   Imports
=========================================================== */
import { library } from '@fortawesome/fontawesome-svg-core'
import { faMapPin, faEnvelope, faPhone, faClock, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { VueTelInput } from 'vue3-tel-input'
import 'vue3-tel-input/dist/vue3-tel-input.css'

import { projects } from "@/data/projectsData.js"

// Assets du hero
import hero1 from '@/assets/hero-slide1.png'
import hero2 from '@/assets/hero-slide2.png'
import hero3 from '@/assets/hero-slide3.png'
import hero4 from '@/assets/hero-slide4.png'
import hero5 from '@/assets/hero-slide5.png'
import hero6 from '@/assets/hero-slide6.png'
import hero7 from '@/assets/hero-slide7.png'

library.add(faMapPin, faEnvelope, faPhone, faClock, faPaperPlane, faWhatsapp)

/* ===========================================================
   Composant
=========================================================== */
export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Projects",
  components: { FontAwesomeIcon, VueTelInput },

  data() {
    return {
      /* -----------------------
         UI / état global
      ----------------------- */
      menuOpen: false,
      activeLink: '#home',
      darkMode: false,

      /* Footer visibility */
      isFooterVisible: false,

      /* Barre de progression */
      scrollProgress: 0,
      navbarHeight: 0,

      /* Données métier */
      projects,

      /* -----------------------
         FORM — FORMSPREE
      ----------------------- */
      FORMSPREE_ENDPOINT: 'https://formspree.io/f/xzzjldqa',

      sending: false,
      statusMsg: "",
      statusOk: false,

      // reCAPTCHA v2
    recaptchaToken: "",
    recaptchaValid: false,

      /* Form state */
      form: {
        name: "",
        email: "",
        message: "",
        subject: "",
        phoneFull: "",   // ce que tape l’utilisateur
        phoneE164: "",   // version normalisée quand dispo (via @validate)
        _gotcha: ""      // honeypot
      },

      /* vue3-tel-input config */
      telCfg: {
        mode: 'international',
        defaultCountry: 'CM',        // fiable sans géoloc.
        autoDefaultCountry: false,
        preferredCountries: ['CM', 'CA', 'FR', 'BE', 'SN', 'CI', 'US', 'GB'],
        dropdownOptions: { showDialCodeInSelection: true, showSearchBox: true },
        inputOptions: { placeholder: 'Numéro (ex. 6xx xx xx xx)', maxlength: 20 },
        validCharactersOnly: true,
        autoFormat: true,
        showDialCode: true,
        // returnFormat: 'E.164', // décommentez si votre version le supporte
      },

      /* Scroll reveal */
      // eslint-disable-next-line vue/no-reserved-keys
      _revealObs: null,

      /* Popover “Nos expertises” */
      openSectorId: null,
      isTouch: false,

      /* Secteurs */
      sectors: [
        {
          id: 'app-dev',
          title: 'Développement d’applications',
          points: [
            'Front-end moderne (Vue/React, SSR/SSG, accessibilité, SEO)',
            'Mobile-first & PWA (responsive, offline, push, React Native / Flutter)',
            'Back-end & API (Node/Express/Nest, Laravel, .NET; REST/GraphQL)',
            'Qualité & sécurité (tests, CI/CD, auth, RGPD, OWASP)',
            'Déploiement & monitoring (Docker, Kubernetes, observabilité)'
          ]
        },
        {
          id: 'cloud-devops',
          title: 'Cloud & DevOps',
          points: [
            'Déploiement automatisé (CI/CD : GitHub Actions, GitLab CI, Jenkins)',
            'Hébergement cloud (AWS, Azure, GCP)',
            'Conteneurs & orchestration (Docker, Kubernetes)',
            'Optimisation des coûts & de la scalabilité'
          ]
        },
        {
          id: 'cyber',
          title: 'Cybersécurité',
          points: [
            'Audits de sécurité (web, apps, réseaux)',
            'Tests d’intrusion (pentests)',
            'Protection des données (RGPD, chiffrement, authentification forte)',
            'Politiques de sécurité (IAM, SSO, MFA)'
          ]
        },
        {
          id: 'ai-data',
          title: 'Intelligence Artificielle & Data',
          points: [
            'Chatbots intelligents',
            'Reconnaissance d’image / NLP',
            'Systèmes de recommandation (e-commerce)',
            'Dashboards analytiques & dataviz avancée'
          ]
        },
        {
          id: 'ecommerce',
          title: 'E-commerce & Transactionnel',
          points: [
            'Boutiques (Shopify, WooCommerce, Magento, sur-mesure)',
            'Paiements (Stripe, PayPal, Square)',
            'Optimisation conversion (UX, funnel, A/B testing)',
            'Automatisation marketing (CRM, email, push)'
          ]
        },
        {
          id: 'iot',
          title: 'IoT (Internet des Objets)',
          points: [
            'Apps connectées (smart home, capteurs, objets)',
            'Collecte & traitement temps réel',
            'Dashboards pour monitorer & contrôler'
          ]
        },
        {
          id: 'training',
          title: 'Formation & Conseil',
          points: [
            'Formations sur-mesure (React, DevOps, Sécurité, Cloud, IA…)',
            'Accompagnement technique pour start-ups & PME',
            'Audit technologique & recommandations'
          ]
        }
      ],

      /* Slider hero */
      heroSlides: [
        { src: hero1, alt: 'Dév web & mobile — SpeDev' },
        { src: hero2, alt: 'UI/UX & Design — SpeDev' },
        { src: hero3, alt: 'Cloud & DevOps — SpeDev' },
        { src: hero4, alt: 'E-commerce & Paiements — SpeDev' },
        { src: hero5, alt: 'IA & Data — SpeDev' },
        { src: hero6, alt: 'Cybersécurité — SpeDev' },
        { src: hero7, alt: 'IoT & Temps réel — SpeDev' },
      ],
      currentSlide: 0,
      autoTimer: null,
      AUTO_DELAY: 6000, // 6s fondu

      // swipe
      drag: { active: false, startX: 0, deltaX: 0, threshold: 42 },

    };
  },

  computed: {
    displayedProjects() {
      const featuredIds = ["AD", "A", "B", "J"];
      return Array.isArray(this.projects)
        ? featuredIds.map(id => this.projects.find(p => p.id === id)).filter(Boolean)
        : [];
    },
    prevIndex() {
      return (this.currentSlide - 1 + this.heroSlides.length) % this.heroSlides.length;
    },
    nextIndex() {
      return (this.currentSlide + 1) % this.heroSlides.length;
    },
    progressStyle() {
      return { width: `${this.scrollProgress}%` };
    },
    isServicesActive() {
      return this.$route.path.startsWith('/services');
    }
  },

  watch: {
    '$route.path'(to) {
      if (to === '/') {
        this.$nextTick(() => {
          this._measureNavbar();
          this._updateProgress();
          this.resetReveal();
          this.initReveal();
          if (window.location.hash) this.scrollTo(window.location.hash);
        });
      } else {
        this.scrollProgress = 0;
        this.activeLink = null;
      }
    }
  },

  mounted() {
    /* Touch? */
    this.isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    // 🔽 AJOUT : callbacks reCAPTCHA accessibles globalement
  window.onSpeDevRecaptcha = (token) => {
    this.recaptchaToken = token;
    this.recaptchaValid = true;
  };

  window.onSpeDevRecaptchaExpired = () => {
    this.recaptchaToken = "";
    this.recaptchaValid = false;
  };
  // 🔼 FIN AJOUT

    /* Footer observer */
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) this.isFooterVisible = true; }),
      { threshold: 0.3 }
    );
    if (this.$refs && this.$refs.footer) obs.observe(this.$refs.footer);

    /* Thème persistant */
    if (localStorage.getItem("darkMode") === "true") {
      this.darkMode = true;
      document.body.classList.add("dark");
    }

    /* Outside click & ESC pour le menu */
    this._outsideHandler = (e) => {
      if (!this.menuOpen) return;
      const insidePanel = e.target.closest('.nav-links');
      const insideBurger = e.target.closest('.hamburger');
      if (!(insidePanel || insideBurger)) this.closeMenu();
    };
    this._escHandler = (e) => {
      if (this.menuOpen && (e.key === 'Escape' || e.key === 'Esc')) this.closeMenu();
    };
    document.addEventListener('pointerdown', this._outsideHandler, true);
    document.addEventListener('keydown', this._escHandler, true);

    /* Navbar sticky + progress bar */
    this._onScrollNavbar = () => {
      const n = this.$refs.navbar;
      if (!n) return;
      if (window.scrollY > 8) n.classList.add('scrolled'); else n.classList.remove('scrolled');
    };
    this._onScrollProgress = () => this._updateProgress();
    this._onResize = () => { this._measureNavbar(); this._updateProgress(); };

    this._measureNavbar();
    this._onScrollNavbar();
    this._updateProgress();

    window.addEventListener('scroll', this._onScrollNavbar, { passive: true });
    window.addEventListener('scroll', this._onScrollProgress, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });

    /* Reveal initial */
    if (this.$route.path === '/') this.initReveal();

    /* Slider autoplay */
    this.startAuto();

    /* Accessibilité polite */
    if (this.$refs.heroSlider && !this.isTouch) {
      this.$refs.heroSlider.setAttribute('aria-live', 'polite');
    }
  },

  beforeUnmount() {
    document.removeEventListener('pointerdown', this._outsideHandler, true);
    document.removeEventListener('keydown', this._escHandler, true);
    window.removeEventListener('scroll', this._onScrollNavbar);
    window.removeEventListener('scroll', this._onScrollProgress);
    window.removeEventListener('resize', this._onResize);
    if (this._revealObs) this._revealObs.disconnect();
    this.stopAuto();
  },

  methods: {
    /* ======================
       NAV / ROUTING
    ====================== */
    toggleMenu() { this.menuOpen = !this.menuOpen; },
    closeMenu() { this.menuOpen = false; },
    openProjects() { this.closeMenu(); this.$router.push('/projects'); },
    openServices() { this.closeMenu(); this.$router.push('/services'); },

    goToAnchor(hash) {
      this.activeLink = hash;
      const navigateAndScroll = () => {
        this.$nextTick(() => {
          if (history.pushState) history.pushState(null, '', hash);
          this.scrollTo(hash);
          this.closeMenu();
        });
      };

      if (this.$route.path !== '/') {
        this.$router.push('/').then(() => {
          this.resetReveal();
          this.initReveal();
          navigateAndScroll();
        });
      } else navigateAndScroll();
    },

    scrollTo(hash) {
      const el = document.querySelector(hash);
      if (!el) return;
      const NAV_OFFSET = Math.max(this.navbarHeight, 88);
      const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
      window.scrollTo({ top: y, behavior: 'smooth' });
    },

    /* ======================
       DARK MODE
    ====================== */
    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      document.body.classList.toggle("dark", this.darkMode);
      localStorage.setItem("darkMode", this.darkMode);
    },

    /* ======================
       UTILS
    ====================== */
    linkify(texte) {
      if (!texte) return "";
      const regex = /https?:\/\/[^\s)]+/g;
      return texte.replace(regex, (url) => {
        const safe = url.replace(/"/g, "&quot;");
        return `<a
              href="${safe}"
              target="_blank"
              rel="noopener noreferrer"
              style="color:#67cada; text-decoration:underline; word-break:break-word;"
            >${url}</a>`;
      });
    },

    /* ======================
       FORM — FORMSPREE
    ====================== */
    onPhoneChange(val) {
      // Certaines versions renvoient un objet { number, ... } si returnObject est actif.
      this.form.phoneFull = typeof val === 'string'
        ? val
        : (val?.number?.international || val?.number || '');
    },

    onPhoneValidate(payload) {
      // Payload possible:
      // { number: { e164, international, national }, isValid, country }  (le plus courant)
      // OU { e164, number, valid } (certaines versions / forks)
      const e164FromObject = payload?.number?.e164;
      const intlFromObject = payload?.number?.international;
      const rawNumber = payload?.number;   // parfois string
      const e164Legacy = payload?.e164;

      const best =
        e164FromObject ||
        e164Legacy ||
        intlFromObject ||
        (typeof rawNumber === 'string' ? rawNumber : '') ||
        this.form.phoneFull ||
        '';

      this.form.phoneE164 = (best || '').trim();
      this.form._phoneValid = Boolean(
        payload?.isValid ?? payload?.valid ?? true  // si l’info n’existe pas, on ne bloque pas
      );
    },

    async sendMessage() {
  const formEl = this.$el.querySelector('.contact__form');
  if (formEl && !formEl.checkValidity()) {
    formEl.reportValidity();
    return;
  }
  if (this.form._gotcha) return;

  // ✅ Vérif reCAPTCHA v2
  if (!this.recaptchaToken || !this.recaptchaValid) {
    this.statusMsg = '⚠️ Merci de cocher la case "Je ne suis pas un robot".';
    this.statusOk = false;
    return;
  }

  // 🔒 Fallback “béton” : numéro E.164 si possible
  const refE164 = (this.$refs.telInput?.getNumber?.('E164')) || '';
  const phoneToSend = (refE164 || this.form.phoneE164 || this.form.phoneFull || '').trim();

  this.sending = true;
  this.statusMsg = '';
  this.statusOk = false;

  try {
    const fd = new FormData();
    fd.append('name', this.form.name);
    fd.append('email', this.form.email);
    fd.append('phone', phoneToSend);
    fd.append('subject', this.form.subject || '');
    fd.append('message', this.form.message);
    fd.append('_gotcha', this.form._gotcha);
    fd.append('_subject', `[SpeDev] ${this.form.subject || 'Nouveau message'}`);
    fd.append('_origin', 'site-spedev');

    // 🔐 Très important : envoyer le token reCAPTCHA à Formspree
    fd.append('g-recaptcha-response', this.recaptchaToken);

    const res = await fetch(this.FORMSPREE_ENDPOINT, {
      method: 'POST',
      body: fd,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      this.statusMsg = err?.errors?.[0]?.message || '❌ Erreur lors de l’envoi. Réessayez plus tard.';
      this.statusOk = false;
      return;
    }

    this.statusOk = true;
    this.statusMsg = '✅ Message envoyé avec succès !';

    // Reset formulaire
    this.form = {
      name: '',
      email: '',
      message: '',
      subject: '',
      phoneFull: '',
      phoneE164: '',
      _gotcha: '',
    };
    this.recaptchaToken = "";
    this.recaptchaValid = false;

    // (optionnel) reset visuel du widget si tu veux
    if (window.grecaptcha) {
      try { window.grecaptcha.reset(); } catch (e) { /* ignore */ }
    }
  } catch (e) {
    console.error(e);
    this.statusMsg = '⚠️ Erreur réseau. Vérifiez votre connexion.';
    this.statusOk = false;
  } finally {
    this.sending = false;
  }
},

    /* ======================
       SCROLL REVEAL
    ====================== */
    resetReveal() {
      if (this._revealObs) { try { this._revealObs.disconnect(); } catch { /* no-op */ } this._revealObs = null; }
      document.querySelectorAll('[data-anim]').forEach(el => el.classList.remove('is-visible'));
    },

    initReveal() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.querySelectorAll('[data-anim]').forEach(el => el.classList.add('is-visible'));
        return;
      }
      const els = Array.from(document.querySelectorAll('[data-anim]'));
      this._revealObs = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });

      els.forEach(el => this._revealObs.observe(el));

      requestAnimationFrame(() => {
        const vh = window.innerHeight * 0.92;
        els.forEach(el => {
          const r = el.getBoundingClientRect();
          if (r.top < vh && r.bottom > 0) el.classList.add('is-visible');
        });
      });
    },

    /* ======================
       PROGRESS BAR
    ====================== */
    _measureNavbar() {
      this.$nextTick(() => {
        const n = this.$refs.navbar;
        this.navbarHeight = n ? n.offsetHeight : 0;
      });
    },

    _updateProgress() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      const doc = document.documentElement;
      const body = document.body;

      const scrollHeight = Math.max(
        body.scrollHeight, body.offsetHeight,
        doc.clientHeight, doc.scrollHeight, doc.offsetHeight
      );

      const view = window.innerHeight;
      const total = Math.max(scrollHeight - view, 1);
      const pct = Math.min(100, Math.max(0, (scrollTop / total) * 100));
      this.scrollProgress = Math.round(pct * 100) / 100;
    },

    /* ======================
       POPOVERS EXPERTISES
    ====================== */
    onHoverIn(id) { if (!this.isTouch) this.openSectorId = id; },
    onHoverOut(id) { if (!this.isTouch && this.openSectorId === id) this.openSectorId = null; },
    onToggleMobile(id) {
      const isOpening = this.openSectorId !== id;
      this.openSectorId = isOpening ? id : null;

      if (isOpening) {
        this.$nextTick(() => {
          const el = document.getElementById(`sector-${id}`);
          if (!el) return;
          const offset = Math.max(this.navbarHeight, 100);
          const y = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: y, behavior: "smooth" });
        });
      }
    },
    closePopover() { this.openSectorId = null; },

    /* ======================
       SLIDER HERO
    ====================== */
    startAuto() { this.stopAuto(); this.autoTimer = window.setInterval(this.nextSlide, this.AUTO_DELAY); },
    stopAuto() { if (this.autoTimer) { window.clearInterval(this.autoTimer); this.autoTimer = null; } },
    pauseAuto() { this.stopAuto(); },
    resumeAuto() { this.startAuto(); },

    nextSlide() { this.currentSlide = (this.currentSlide + 1) % this.heroSlides.length; },
    prevSlide() { this.currentSlide = (this.currentSlide - 1 + this.heroSlides.length) % this.heroSlides.length; },
    goTo(i) { if (i >= 0 && i < this.heroSlides.length) this.currentSlide = i; },

    // Swipe
    onPointerDown(e) {
      this.drag.active = true;
      this.drag.startX = e.clientX ?? (e.touches?.[0]?.clientX ?? 0);
      this.drag.deltaX = 0;
      this.pauseAuto();
    },
    onPointerMove(e) {
      if (!this.drag.active) return;
      const x = e.clientX ?? (e.touches?.[0]?.clientX ?? 0);
      this.drag.deltaX = x - this.drag.startX;
    },
    onPointerUp() {
      if (!this.drag.active) return;
      const { deltaX, threshold } = this.drag;
      this.drag.active = false;
      if (deltaX > threshold) this.prevSlide();
      else if (deltaX < -threshold) this.nextSlide();
      this.resumeAuto();
      this.drag.deltaX = 0;
    },
  },
};
</script>


<style scoped>
.status {
  margin-top: 10px;
  font-weight: 700;
}

.status--ok {
  color: #16a34a;
}

.status--err {
  color: #b91c1c;
}

/* ----------------------------------------------------------
   RESET & BASE
----------------------------------------------------------- */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif
}

html {
  scroll-behavior: smooth;
  overflow-x: clip
}

body {
  background: #f8f8f8;
  transition: background-color .3s ease, color .3s ease
}

/* ----------------------------------------------------------
   NAVBAR
----------------------------------------------------------- */
.navbar,
.logo-container,
.logo,
.nav-links {
  box-sizing: border-box
}

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #fff;
  border-bottom: 1px solid #eaeaea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
}

.logo-container {
  display: flex;
  align-items: center;
  gap: .75rem
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px
}

.logo img {
  height: 80px;
  width: auto;
  border-radius: 8px;
  transition: transform .25s
}

.logo img:hover {
  transform: scale(1.04)
}

.btn-darkmode {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 6px;
  line-height: 0;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: transparent;
  color: #111;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04);
  transition: .2s
}

.btn-darkmode:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(16, 24, 40, .08)
}

.btn-darkmode svg {
  width: 25px;
  height: 25px;
  display: block
}

.darkmode-label {
  margin-left: 8px;
  font-weight: 700;
  color: #374151;
  font-size: .95rem
}

.nav-links {
  display: flex;
  align-items: center;
  list-style: none;
  gap: 2rem;
  transition: transform .3s
}

.nav-links li a {
  position: relative;
  display: inline-block;
  text-decoration: none;
  color: #111;
  font-weight: 600;
  padding: .25rem 0
}

.nav-links li a:hover,
.nav-links li a.active {
  color: #67cada
}

.nav-links li a.active::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -10px;
  width: 42px;
  height: 3px;
  border-radius: 3px;
  background: #67cada
}

.btn-projects {
  background: #67cada;
  border: none;
  padding: .65rem 1.05rem;
  color: #fff;
  font-weight: 700;
  border-radius: 12px 999px 999px 12px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(103, 202, 218, .25);
  transition: .2s
}

.btn-projects:hover {
  background: #50b5c4;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(103, 202, 218, .35)
}

.hamburger {
  display: none;
  flex-direction: column;
  cursor: pointer;
  gap: 6px
}

.hamburger span {
  height: 3px;
  width: 26px;
  background: #111;
  border-radius: 3px;
  transition: all .3s
}

.hamburger span.rotate1 {
  transform: rotate(45deg) translate(5px, 5px)
}

.hamburger span.fade {
  opacity: 0
}

.hamburger span.rotate2 {
  transform: rotate(-45deg) translate(5px, -5px)
}

@media (max-width:768px) {
  .logo img {
    height: 44px;
    border-radius: 6px
  }

  .nav-links {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 280px;
    background: #fff;
    flex-direction: column;
    align-items: flex-start;
    padding: 4.75rem 1.5rem 2rem;
    gap: 1rem;
    transform: translateX(100%);
    box-shadow: -2px 0 18px rgba(0, 0, 0, .08);
    z-index: 1001
  }

  .nav-links.nav-active {
    transform: translateX(0)
  }

  .hamburger {
    display: flex
  }

  .li-darkmode {
    display: flex;
    align-items: center;
    gap: .5rem
  }

  .darkmode-label {
    display: inline
  }
}

@media (min-width:769px) {
  .li-darkmode {
    order: -1
  }

  .darkmode-label {
    display: none
  }
}

/* ===== Barre de progression sous la navbar ===== */
.nav-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 3px;
  pointer-events: none;
}

.nav-progress__bar {
  display: block;
  width: 0%;
  height: 100%;
  background: linear-gradient(90deg, #67cada, #7fd8e5);
  box-shadow: 0 2px 8px rgba(103, 202, 218, .35);
  transition: width .15s ease-out;
  will-change: width;
}

body.dark .nav-progress__bar {
  background: linear-gradient(90deg, #67cada, #5fc1d0);
  box-shadow: 0 2px 8px rgba(0, 0, 0, .45);
}

/* ----------------------------------------------------------
   DARK MODE
----------------------------------------------------------- */
body.dark {
  background-color: #0f1115;
  color: #e6e8ec
}

body.dark .navbar {
  background-color: #0f1115;
  border-bottom: 1px solid #222633
}

body.dark .nav-links li a {
  color: #e6e8ec
}

body.dark .nav-links li a:hover {
  color: #67cada
}

body.dark .btn-darkmode {
  background: #0b0d12;
  border-color: #232838;
  color: #e6e8ec
}

body.dark .btn-projects {
  background: #67cada
}

body.dark .btn-projects:hover {
  background: #50b5c4
}

/* ----------------------------------------------------------
   MAIN 
----------------------------------------------------------- */
main {
  min-height: 70vh;
  padding: 1rem;
  padding-top: 70px
}

/* ==========================================================
   FOOTER 
========================================================== */
.footer {
  background: #0b0d12;
  color: #cbd5e1;
  padding: 44px 16px 20px;
}

.footer__grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 860px) {
  .footer__grid {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
}

.footer__title {
  color: #ffffff;
  font-weight: 800;
  font-size: 1.05rem;
  margin: 0 0 12px;
}

.footer__links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer__links li {
  margin: 8px 0;
}

.footer__links a {
  color: #cbd5e1;
  text-decoration: none;
  transition: color .2s ease, opacity .2s ease;
}

.footer__links a:hover,
.footer__links a:focus-visible {
  color: #ffffff;
  text-decoration: underline;
  outline: none;
}

.footer__contact {
  --icon-gutter: 28px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer__contact .icon-line {
  position: relative;
  display: block;
  padding-left: 0;
  margin: 8px 0;
  min-height: 24px;
  line-height: 1.4;
}

.footer__contact .icon-line> :first-child {
  position: absolute;
  left: calc(var(--icon-gutter) * -1);
  top: 2px;
  width: 20px;
  height: 20px;
  display: grid;
  place-items: center;
  line-height: 0;
}

.footer__contact .icon-line .wa-icon,
.footer__contact .icon-line svg,
.footer__contact .icon-line img {
  width: 20px;
  height: 20px;
  display: block;
}

.footer__contact .icon-line a,
.footer__contact .icon-line span {
  display: block;
  margin: 0;
  color: #cbd5e1;
  text-decoration: none;
  word-break: break-word;
}

.footer__contact .icon-line a:hover {
  color: #ffffff;
  text-decoration: underline;
}

.wa-icon {
  color: #67cada;
}

@media (max-width: 520px) {
  .footer__contact {
    --icon-gutter: 24px;
  }
}

.footer__bottom {
  max-width: 1200px;
  margin: 20px auto 0;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  text-align: left;
  color: #94a3b8;
  font-size: .95rem;
}

.footer a:focus-visible {
  outline: 2px solid rgba(103, 202, 218, 0.6);
  outline-offset: 2px;
  border-radius: 6px;
}

.footer__contact .icon-line:hover {
  opacity: .95;
}

body.dark .footer {
  background: #0f1115;
  color: #d6d9e0;
}

body.dark .footer__bottom {
  border-top-color: rgba(255, 255, 255, 0.10);
  color: #a5b0c2;
}

body.dark .footer__links a,
body.dark .footer__contact .icon-line a,
body.dark .footer__contact .icon-line span {
  color: #d6d9e0;
}

body.dark .footer__links a:hover,
body.dark .footer__links a:focus-visible,
body.dark .footer__contact .icon-line a:hover,
body.dark .footer__contact .icon-line a:focus-visible {
  color: #ffffff;
}

/* ----------------------------------------------------------
   HERO
----------------------------------------------------------- */
.hero--spe {
  position: relative;
  text-align: center;
  padding: clamp(40px, 5vw, 64px) 16px 64px;
  overflow: visible;
  /* background:
    radial-gradient(1200px 400px at 0% 20%, rgba(103, 202, 218, .10), transparent 60%),
    radial-gradient(800px 300px at 100% 10%, rgba(103, 202, 218, .08), transparent 60%),
    #f8fafc; */
}

/* ===== SLIDER  ===== */
.hero-slider {
  position: relative;
  max-width: 1200px;
  margin: 0 auto 22px;
  outline: none;
}

.hero-slider__track {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  background: #0b0d12;
  box-shadow: 0 16px 36px rgba(2, 12, 27, .18);
  height: clamp(220px, 38vw, 520px);
  isolation: isolate;
}

/* Slides en fondu */
.hero-slider__slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  z-index: 0;
  transition: opacity 900ms cubic-bezier(.22, .61, .36, 1), filter 900ms cubic-bezier(.22, .61, .36, 1);
  will-change: opacity, filter;
  pointer-events: none;
}

.hero-slider__slide.is-active {
  opacity: 1;
  z-index: 1;
}

.hero-slider .hero__banner {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  background: transparent;
  display: block;
  backface-visibility: hidden;
  transform: translateZ(0);
}

.hero-slider__slide.is-active .hero__banner {
  animation: none;
}

/* Accessibilité */
.sr-only {
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Ken Burns léger */
@keyframes spedev-kenburns {
  from {
    transform: scale(1)
  }

  to {
    transform: scale(1.03)
  }
}

.hero-slider__slide.is-active .hero__banner {
  animation: spedev-kenburns 6s ease-in-out both;
}

/* Flèches rondes */
.hero-slider__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, .92);
  color: #0b1324;
  font-size: 22px;
  font-weight: 700;
  line-height: 0;
  display: none !important;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(2, 12, 27, .10);
  transition: transform .2s, box-shadow .2s, background .2s, border-color .2s;
}

.hero-slider__arrow:hover {
  transform: translateY(-50%) scale(1.04);
  background: #fff;
  border-color: rgba(103, 202, 218, .65);
  box-shadow: 0 12px 24px rgba(103, 202, 218, .20);
}

.hero-slider__arrow--prev {
  left: 12px
}

.hero-slider__arrow--next {
  right: 12px
}

.hero-slider__dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  padding: 6px 0;
}

.hero-slider__dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .7);
  border: 1px solid rgba(0, 0, 0, .08);
  cursor: pointer;
  transition: transform .2s, background .2s, box-shadow .2s;
}

.hero-slider__dot.is-active {
  background: #67cada;
  box-shadow: 0 0 0 3px rgba(103, 202, 218, .22);
  transform: scale(1.12);
}

/* Dark mode slider bits */
body.dark .hero-slider__arrow {
  background: rgba(15, 17, 21, .9);
  color: #e6e8ec;
  border-color: #232838;
}

body.dark .hero-slider__arrow:hover {
  background: #0b0d12;
  border-color: #2a3b45;
  box-shadow: 0 12px 24px rgba(0, 0, 0, .45);
}

body.dark .hero-slider__dot {
  background: rgba(255, 255, 255, .28);
  border-color: rgba(255, 255, 255, .06);
}

.hero__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #eefbfd;
  color: #2b3a42;
  border: 1px solid #dff3f7;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 700;
  font-size: .95rem;
  box-shadow: 0 6px 14px rgba(2, 12, 27, .06);
  margin: 12px 0 16px;
}

.hero__title {
  font-size: clamp(32px, 5vw, 56px);
  line-height: 1.05;
  font-weight: 800;
  color: #0b1324;
  margin: 2px auto 10px
}

.hero__title-accent {
  color: #67cada
}

.hero__subtitle {
  max-width: 860px;
  margin: 0 auto 22px;
  color: #475569;
  font-size: clamp(16px, 2vw, 20px)
}

.hero__block-title {
  font-size: clamp(20px, 2.5vw, 26px);
  color: #0b1324;
  margin: 8px 0 10px;
  font-weight: 800
}

/* ----------------------------------------------------------
   BENEFITS
----------------------------------------------------------- */
.hero__benefits {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  max-width: 980px;
  margin: 12px auto 26px;
}

@media (min-width:760px) {
  .hero__benefits {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width:1080px) {
  .hero__benefits {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.benefit {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e8eef2;
  padding: 16px;
  /* (inchangé) */
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(2, 12, 27, .06);
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  transition: transform .18s, box-shadow .18s, border-color .18s;
}

.benefit:hover {
  transform: translateY(-2px);
  border-color: rgba(103, 202, 218, .6);
  box-shadow: 0 10px 24px rgba(2, 12, 27, .10);
}

.benefit:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(103, 202, 218, .35);
}

.benefit__head {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.benefit__head>svg {
  display: none;
}

.benefit__title {
  font-weight: 800;
  text-align: center;
  margin: 0 auto;
  overflow-wrap: anywhere;
  hyphens: auto;
}

/* ==== DESKTOP/TABLET (inchangé) : popover au-dessus de la carte ==== */
@media (min-width:761px) {
  .benefit__popover {
    position: absolute;
    inset: auto auto 100% 0;
    transform: translateY(-10px);
    min-width: min(480px, 92vw);
    max-width: 520px;
    background: #0b1324;
    color: #fff;
    border: 1px solid #1f2a44;
    border-radius: 14px;
    padding: 14px 14px 10px;
    box-shadow: 0 18px 50px rgba(2, 12, 27, .35);
    opacity: 0;
    pointer-events: none;
    z-index: 10;
    transition: opacity .18s, transform .18s;
  }

  .benefit__popover--visible {
    opacity: 1;
    transform: translateY(-14px);
    pointer-events: auto;
  }
}

/* Contenu popover (commun) */
.benefit__list {
  margin-left: 18px;
  display: grid;
  gap: 6px;
  text-align: left;
}

.benefit__list li {
  font-weight: 500;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.benefit__close {
  margin-top: 10px;
  border: 1px solid rgba(255, 255, 255, .18);
  background: transparent;
  color: #fff;
  padding: 6px 10px;
  border-radius: 10px;
  cursor: pointer;
}

.benefit__close:hover {
  background: rgba(255, 255, 255, .06);
}

/* ==== MOBILE ONLY : popover entièrement À L’INTÉRIEUR ==== */
@media (max-width:760px) {
  .benefit {
    align-items: stretch;
    flex-direction: column;
    --pad: 16px;
    padding: var(--pad);
    overflow: visible;
    /* le popover fait partie du flux */
  }

  .benefit__popover {
    position: static !important;
    /* accordéon dans la carte */
    inset: auto !important;
    transform: none !important;
    width: 100%;
    margin: 10px 0 0;
    padding: 18px 20px 72px;
    /* rembourrage confortable */
    max-height: 72vh;
    background: #0b1324;
    color: #fff;
    border: 1px solid #1f2a44;
    border-radius: 12px;
    box-shadow: 0 18px 50px rgba(2, 12, 27, .35);
    box-sizing: border-box;
    opacity: 0;
    overflow: auto;
    pointer-events: none;
    -webkit-overflow-scrolling: touch;
  }

  .benefit__popover--visible {
    opacity: 1;
    pointer-events: auto;
  }

  /* Ajustements mobiles utiles */
  html,
  body,
  #app {
    overflow-x: hidden;
  }

  .hero--spe,
  .hero__benefits,
  .benefit {
    overflow-x: hidden;
    contain: paint;
  }

  .benefit__list {
    margin-left: 0;
    padding-left: 18px;
  }

  .hero__benefits {
    padding-inline: 8px;
  }

  .hero--spe {
    padding-bottom: 86px;
    overflow: visible;
  }
}

/* ---- Dark mode (identique à avant) ---- */
body.dark .benefit {
  background: #0b0d12;
  border-color: #232838;
  color: #e6e8ec;
}

body.dark .benefit:hover {
  border-color: #2a3b45;
  box-shadow: 0 16px 36px rgba(0, 0, 0, .5);
}

/* ==== Fix "Découvrir nos projets" (forcer style et couche) ==== */
.hero__cta {
  position: relative;
  /* évite d’être recouvert */
  z-index: 2;
  /* passe devant les popovers */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #fff;
  font-weight: 800;
  padding: 14px 22px;
  border-radius: 14px;
  margin-top: 30px;
  border: 0;
  background: #67cada;
  box-shadow: 0 12px 30px rgba(103, 202, 218, .35);
  transition: transform .2s, box-shadow .2s, background .2s;
}

.hero__cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 40px rgba(103, 202, 218, .45);
  background: #50b5c4;
}

/* (optionnel) si un parent coupe l’ombre du bouton */
.hero--spe {
  overflow: visible;
}

@media (min-width:1080px) {
  .hero__benefits>.benefit:last-child:nth-child(3n + 1) {
    grid-column: 2;
    /* place au milieu */
  }
}

@media (min-width:760px) and (max-width:1079px) {
  .hero__benefits>.benefit:last-child:nth-child(2n + 1) {
    grid-column: 1 / -1;
    /* occupe les 2 colonnes */
    justify-self: center;
    /* centre la carte */
    max-width: 560px;
    /* largeur confortable (optionnel) */
  }
}


/* ----------------------------------------------------------
   ABOUT
----------------------------------------------------------- */
.spedev-abt-section {
  /* background: transparent; */
  padding: 72px 0
}

.spedev-abt-wrap {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center
}

.spedev-abt-badge {
  display: inline-block;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(103, 202, 218, .15);
  color: #67cada;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: .2px
}

.spedev-abt-title {
  margin: 14px auto 12px;
  font-size: clamp(28px, 5vw, 48px);
  line-height: 1.1;
  color: #0f172a;
  font-weight: 800;
  max-width: 940px
}

.spedev-abt-title-accent {
  color: #67cada
}

.spedev-abt-subtitle {
  margin: 0 auto 32px;
  color: #475569;
  font-size: 18px;
  max-width: 820px
}

.spedev-abt-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  margin-top: 8px
}

@media (min-width:820px) {
  .spedev-abt-grid {
    grid-template-columns: repeat(3, 1fr)
  }
}

.spedev-abt-card {
  text-align: left;
  border-radius: 18px;
  padding: 24px;
  background: #f8fafc;
  transition: transform .2s, box-shadow .2s;
  box-shadow: 0 1px 0 rgba(2, 6, 23, .04), 0 10px 24px -16px rgba(2, 6, 23, .18)
}

.spedev-abt-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px -16px rgba(2, 6, 23, .22)
}

.spedev-abt-illus {
  color: #67cada;
  margin: 6px 0 12px
}

.spedev-abt-card-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px
}

.spedev-abt-card-text {
  font-size: 15.5px;
  color: #475569;
  line-height: 1.6
}

.spedev-abt-card--mint {
  background: #e6fbf9
}

.spedev-abt-card--lilac {
  background: #f0f7fa
}

.spedev-abt-card--blue {
  background: #d8f2f7
}

.spedev-abt-card--rose {
  background: #f3fafc
}

.spedev-abt-card--sage {
  background: #e0f6f7
}

.spedev-abt-card--sky {
  background: #c9edf3
}

.spedev-abt-cta {
  margin-top: 28px;
  padding: 16px 20px;
  background: #e6fbf9;
  border-radius: 14px;
  color: #0f172a;
  font-weight: 600
}

/* ----------------------------------------------------------
   PROJECTS
----------------------------------------------------------- */
.projects {
  padding: clamp(48px, 6vw, 72px) 20px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center
}

.project-chip {
  display: inline-block;
  background: #eaf2ff;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 700;
  font-size: .95rem;
  margin-bottom: 14px;
  color: #67cada;
  border: 1px solid #dff0ff
}

.projects__title {
  font-size: clamp(32px, 5vw, 56px);
  line-height: 1.05;
  margin: 0 0 12px;
  font-weight: 800
}

.projects__title-accent {
  color: #67cada
}

.projects__subtitle {
  max-width: 860px;
  margin: 0 auto 24px;
  color: #475569;
  font-size: clamp(14px, 2vw, 16px)
}

.projects__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 22px;
  align-items: stretch
}

.project {
  position: relative;
  background: #fff;
  border: 1px solid #e8eef2;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(2, 12, 27, .06);
  transition: .25s
}

.project:hover {
  transform: translateY(-4px);
  border-color: rgba(103, 202, 218, .45);
  box-shadow: 0 16px 36px rgba(103, 202, 218, .18)
}

.project__thumb {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: #eef6f8
}

.project__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .35s, filter .35s
}

.project:hover .project__thumb img {
  transform: scale(1.03);
  filter: saturate(1.05)
}

.project__body {
  text-align: left;
  padding: 16px;
  display: grid;
  gap: 10px;
  align-content: start
}

.project__title {
  font-weight: 800;
  font-size: 1.05rem;
  line-height: 1.25;
  color: #0b1324;
  min-height: 2.6em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden
}

.project__desc {
  color: #4b5563;
  font-size: .95rem;
  line-height: 1.5;
  min-height: 4.5em;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden
}

.cta {
  text-decoration: none
}

.project__cta {
  justify-self: start;
  margin-top: 4px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #67cada;
  color: #fff;
  font-weight: 700;
  font-size: .92rem;
  box-shadow: 0 8px 18px rgba(103, 202, 218, .28);
  transition: .2s
}

.project:hover .project__cta {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(103, 202, 218, .38);
  background: #50b5c4
}

.projects__more {
  margin-top: 24px
}

.btn-more {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 14px;
  padding: 12px 18px;
  font-weight: 800;
  text-decoration: none;
  color: #374159;
  background: #e9f7fa;
  border: 1px solid #cbeef4;
  transition: .2s
}

.btn-more:hover {
  transform: translateY(-1px);
  background: #dff3f7;
  box-shadow: 0 12px 26px rgba(103, 202, 218, .18)
}

@media (max-width:520px) {
  .projects__title {
    font-size: clamp(26px, 7vw, 34px)
  }

  .project__body {
    padding: 14px
  }

  .project__title {
    font-size: 1rem
  }

  .project__desc {
    -webkit-line-clamp: 4
  }
}

body.dark .projects__subtitle {
  color: #cbd5e1
}

body.dark .project {
  background: #0b0d12;
  border-color: #232838;
  box-shadow: 0 6px 18px rgba(0, 0, 0, .35)
}

body.dark .project__title {
  color: #e6e8ec
}

body.dark .project__desc {
  color: #cbd5e1
}

body.dark .project:hover {
  border-color: #2a3b45;
  box-shadow: 0 16px 36px rgba(0, 0, 0, .5)
}

body.dark .btn-more {
  background: #0b0d12;
  color: #e6e8ec;
  border-color: #232838
}

body.dark .btn-more:hover {
  background: #121826;
  color: #fff
}

/* ----------------------------------------------------------
   CONTACT
----------------------------------------------------------- */
:root {
  --grad: linear-gradient(90deg, #2b6fff 0%, #7a5cff 100%);
  --blue: #67cada;
  --blue-50: #eaf2ff;
  --text: #0f172a;
  --muted: #4b5563;
  --line: #e5e7eb;
  --input: #f6f8fb;
  --radius: 16px;
  --shadow: 0 12px 30px rgba(2, 12, 27, .08)
}

.contact-grad {
  background: var(--grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: #67cada
}

.contact-hero {
  text-align: center;
  padding: 80px 16px 56px;
  color: var(--text)
}

.contact-chip {
  display: inline-block;
  background: #eaf2ff;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 600;
  font-size: .9rem;
  margin-bottom: 14px;
  color: #67cada
}

.contact-title {
  font-size: clamp(32px, 5vw, 56px);
  line-height: 1.05;
  margin: 0 0 12px;
  font-weight: 800
}

.contact-subtitle {
  max-width: 860px;
  margin: 0 auto 10px;
  color: #4b5563;
  font-size: 1.35rem
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 12px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  transition: background .3s;
  margin-top: 5px
}

.btn-grad {
  background: #67cada;
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 123, 255, .3);
  font-size: 1em
}

.btn-grad:hover {
  background: #50b5c4
}

.contact {
  padding: 24px 16px 70px;
  color: var(--text)
}

.contact__grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr
}

@media (min-width:980px) {
  .contact__grid {
    grid-template-columns: 460px 1fr
  }
}

.contact__info {
  background: #e8f1ff;
  border: 1px solid #d8e6ff;
  border-radius: 18px;
  padding: 30px 24px 30px 35px;
  box-shadow: var(--shadow)
}

.contact__heading {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 6px 0 18px
}

.sep {
  border: none;
  height: 1px;
  background: rgba(2, 12, 27, .07);
  margin: 18px 0 12px
}

.info-block {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 14px 0
}

.info-block1 {
  display: block;
  margin: 14px 0
}

.coor {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 14px 0
}

.info-block__icon {
  margin-top: 2px
}

.info-block__label {
  font-weight: 700;
  margin-bottom: 4px
}

.info-block__list {
  margin: 4px 0 0;
  padding-left: 18px
}

.info-block__list.compact {
  list-style: none;
  padding-left: 0
}

.link {
  color: var(--blue);
  text-decoration: none
}

.link:hover {
  text-decoration: underline
}

.bullet {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: var(--blue);
  color: #fff;
  display: grid;
  place-items: center;
  box-shadow: 0 6px 18px rgba(37, 99, 235, .25)
}

.bullet--pin {
  background-color: #67cada
}

.bullet1 {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #fff;
  display: grid;
  place-items: center;
  color: #67cada;
  box-shadow: 0 6px 18px rgba(37, 99, 235, .25)
}

.wa-icon {
  color: #67cada
}

/* vue3-tel-input */
:deep(.vue-tel-input) {
  width: 100%;
  background: var(--input);
  border: 1px solid #eef1f5;
  border-radius: 12px;
  padding: 0 8px;
  min-height: 44px;
  display: flex;
  align-items: center;
  box-shadow: none
}

:deep(.vue-tel-input:hover) {
  border-color: #dfe7fb
}

:deep(.vue-tel-input:focus-within) {
  border-color: #cbdafe;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .12)
}

:deep(.vue-tel-input .vti__dropdown) {
  border-right: 1px solid #eef1f5;
  margin-right: 8px;
  padding-right: 8px;
  background: transparent
}

:deep(.vue-tel-input .vti__selection) {
  background: transparent;
  border: none;
  box-shadow: none
}

:deep(.vue-tel-input .vti__input) {
  height: 42px;
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 1rem;
  color: inherit
}

:deep(.vue-tel-input .vti__dropdown-list) {
  z-index: 1000
}

/* Form */
.contact__form {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, .1);
  border-radius: 18px;
  padding: 26px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .15)
}

.form__title {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0 0 18px
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px
}

@media (min-width:760px) {
  .grid-2 {
    grid-template-columns: 1fr 1fr
  }
}

.field label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px
}

.req {
  color: #ef4444
}

.field input,
.field textarea {
  width: 100%;
  background: var(--input);
  border: 1px solid #eef1f5;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 1rem;
  outline: none;
  transition: .2s
}

.field textarea {
  resize: vertical
}

.field input::placeholder,
.field textarea::placeholder {
  color: #98a2b3
}

.field input:focus,
.field textarea:focus {
  border-color: #cbdafe;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .12)
}

/* ----------------------------------------------------------
   SCROLL REVEAL
----------------------------------------------------------- */
[data-anim] {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity .7s cubic-bezier(.22, .61, .36, 1), transform .7s cubic-bezier(.22, .61, .36, 1), filter .7s cubic-bezier(.22, .61, .36, 1);
  transition-delay: var(--d, 0ms);
  will-change: opacity, transform, filter
}

[data-anim].is-visible {
  opacity: 1;
  transform: none;
  filter: none
}

[data-anim="fade"] {
  transform: none
}

[data-anim="fade-up"] {
  transform: translateY(18px)
}

[data-anim="slide-left"] {
  transform: translateX(28px)
}

[data-anim="slide-right"] {
  transform: translateX(-28px)
}

[data-anim="zoom-in"] {
  transform: scale(.96)
}

@media (prefers-reduced-motion:reduce) {
  [data-anim] {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
    filter: none !important
  }
}

/* --- Fix mobile : padding global + icônes visibles --- */
@media (max-width: 520px) {
  .footer {
    padding: 44px 20px 20px;
  }

  .footer__contact .icon-line {
    padding-left: 28px;
  }

  .footer__contact .icon-line> :first-child {
    left: 0;
  }
}

/* ==== Footer — interlignes & espacement sur les lignes de contact ==== */
.footer__contact .footer-line {
  line-height: 1.9;
  margin: 6px 0;
}

.footer__contact .footer-line--wa {
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.9;
  margin: 6px 0;
}

.footer__contact .footer-line__icon {
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}

.footer__contact .footer-line a,
.footer__contact .footer-line span {
  display: inline-block;
  vertical-align: middle;
}

@media (max-width: 520px) {

  .footer__contact .footer-line,
  .footer__contact .footer-line--wa {
    line-height: 2.0;
    margin: 7px 0;
  }
}

/* Ancre confortable sous navbar sticky */
#home,
#about,
#projects,
#contact1 {
  scroll-margin-top: 90px
}
</style>
